/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';
import { replaceArray, deleteFromArray } from 'util/ArrayOperations';
import { fetchFieldPromises } from 'util/FetchSmartsData';
import { fromPromise,  FULFILLED } from 'mobx-utils';
const MAX_RECENT_FIELDS = 5;

const maybeNull = (type) => types.union( type, types.undefined );

const FieldsMapEntry = types.model({
	focusFieldId: maybeNull(types.string),
	recentFields: types.optional( types.array(types.string), [] )
})
	.volatile(()=>({
		all: null,
		interesting: null,
		originalInteresting: null 	//Deprecated - remove with newStartingPoints flag
	}))

	.views((self) => ({
	/**
	 * Utility to get all fields - throws error if promise is not resolved
	 * @return {object[]} all fields
	 */
		getAll: () => {
			if(self.all.state !== FULFILLED) {
				throw new Error('Cannot get all fields');
			}
			return self.all.value;
		},
		/**
	 * Utility to get interesting fields - throws error if promise is not resolved
	 * @return {object[]} interesting fields
	 */
		getInteresting: () => {
			if(self.interesting.state !== FULFILLED) {
				throw new Error('Cannot get interesting fields');
			}
			return self.interesting.value;
		},

		_getField(fieldId) {
			return self.getAll().find(({ id }) => id === fieldId);
		},

		getRecentFields() {
			return self.recentFields.map(id => self._getField(id));
		}
	})).actions(self => ({
		setFocusField(fieldId) {
			self.focusFieldId = fieldId;
		},
		setAll(all) {
			self.all = all;
		},
		setInteresting(interesting) {
			self.interesting = interesting;
		},
		setOriginalInteresting(original) {
			self.originalInteresting = original;
		},
		addRecentField(fieldId) {
			const index = self.recentFields.indexOf(fieldId);

			/* if the field is already in the list
		 * splice removes it from its position and shifts the list
		 */
			if(~index) {
				self.recentFields.splice(index,1);
			}
			/*always add new field to the beginning of the list*/
			self.recentFields.unshift(fieldId);
			if(self.recentFields.length > MAX_RECENT_FIELDS) {
				self.recentFields.splice(MAX_RECENT_FIELDS);
			}
			return true;
		}
	}));

const FieldsMap = types.map( FieldsMapEntry );

export const FieldsModel = types
	.model('FieldsModel', {
		//clearInput is used to trigger a reaction in the fieldInput, when it is set to true, we will clear the input box
		clearInput: types.optional(types.boolean, false),
		fields: types.maybeNull( FieldsMap, types.undefined)
	})
	.volatile(()=>({
		_mobxDisposeEvents : []
	}))
	.extend((self) => {
		function getDataSourceId() {
			return getEnv(self).exploreVM.activeDataSourceId;
		}

		function setDefaultFocusFieldId() {
			self.setFocusFieldIndex(self.isNewStartingPointsFeatureEnabled() ? null : 0);
		}

		return {
			views: {
				isNewStartingPointsFeatureEnabled() {
					return !getEnv(self).isFeatureEnabled('newStartingPoints', 'false');
				},

				_getFocusFieldId() {
					const fields = self.getFields();
					if(fields && fields.focusFieldId) {
						return fields.focusFieldId;
					}
					//When turning off newStartingPoints flag, delete this clause:
					if(!self.isNewStartingPointsFeatureEnabled() && fields.interesting.state === FULFILLED) {
						return fields.getInteresting()[0];
					}
					return null;
				},

				_getField(fieldId) {
					return self.getFields()._getField(fieldId);
				},

				/**
				 * @return {number} the index of focused interesting field for the active datasource ID
				 * @deprecated Use getFocusField(), this will be going away when newStartingPoints flag is removed
				 */
				getFocusFieldIndex() {
					const focusId = self._getFocusFieldId();
					if(!focusId) {
						return -1;
					}
					return self.getFieldIndex(focusId);
				},

				/**
				 *
				 * @return {Object} the focused interesting field for the active datasource ID
				 */
				getFocusField() {
					const focusId = self._getFocusFieldId();
					return focusId && self._getField(focusId);
				},

				/**
				 * For a given datasource ID, get a JSON object that has all column fields, interesting fields, original
				 * interesting fields and focused field index
				 * @param [string] dataSourceId data source module ID, by default it is the active DataSource Id
				 * @return {Object}
				 */
				getFields(dataSourceId = getDataSourceId()) {
					if(!dataSourceId) {
						return {};
					}
					let fields = self.fields.get(dataSourceId);
					if(!fields) {
						const { all, interesting } = fetchFieldPromises(getEnv(self).exploreVM, dataSourceId);
						fields = FieldsMapEntry.create({});
						fields.setAll(fromPromise(all));
						fields.setInteresting(fromPromise(interesting));
						fields.setOriginalInteresting(fromPromise(interesting));

						if(!self.isNewStartingPointsFeatureEnabled()) {
							interesting.then(setDefaultFocusFieldId);
						}

						self._setFields( dataSourceId, fields );
					}
					return fields;
				},

				isSetFocusFieldFeatureEnabled() {
					return !getEnv(self).isFeatureEnabled('setFocusField', 'false');
				},

				/**
				 * @param {string} fieldId
				 * @return `true` iff `fieldId` is the id of a known field in the currently selected data source
				 */
				isValidField(fieldId) {
					return !!self._getField(fieldId);
				},

				/**
				 * @param {string} fieldId
				 * @return `true` iff `fieldId` can be set as the focus field
				 */
				canSetFocusField(fieldId) {
					if(!self.isSetFocusFieldFeatureEnabled()) {
						return false;
					}
					if(!self.isValidField(fieldId)) {
						return false;
					}
					return fieldId !== self._getFocusFieldId();
				},

				/**
				 * @param {string} fieldId
				 * @return the index in the displayed fields of `fieldId`, or `-1` if not displayed
				 * @deprecated this will be going away when the newStartingPoints flag is removed
				 */
				getFieldIndex(fieldId) {
					return self.getFields().getInteresting().map(({ id }) => id).indexOf(fieldId);
				},

				hasFetchedFields() {
					return self.fields.has(getDataSourceId());
				}
			},
			actions: {
				afterCreate(){
					if( !self.fields ) {
						self.fields = FieldsMap.create({});
					}
				},
				beforeDestroy() {
					// Remove the events.
					self._mobxDisposeEvents.forEach(dispose => dispose());
				},

				/**
				 * @deprecated Use setFocusField(), this will be going away when newStartingPoints flag is removed
				 */
				setFocusFieldIndex(fieldIndex) {
					const fields = self.getFields();
					if(fields.interesting.state === FULFILLED) {
						const field = fields.getInteresting()[fieldIndex];
						if(field) {
							fields.setFocusField(field.id);
						}
					}
				},

				/**
				 * Makes the specified field the focus field; adds it to the list of
				 * displayed fields if necessary.
				 * @param {string} fieldId
				 */
				setFocusField(fieldId) {
					const fields = self.getFields();
					if (self.canSetFocusField(fieldId)) {
						fields.setFocusField(fieldId);
						fields.addRecentField(fieldId);
						return true;
					}
					return false;
				},

				/**
				 * @deprecated This will be going away when newStartingPoints flag is removed
				 */
				setInterestingFields(newFields) {
					replaceArray(self.getFields().getInteresting(), newFields);
					if (newFields.length > 0) {
						// The first field is the focus field by default
						self.setFocusFieldIndex(0);
					}
				},

				/**
				 * @deprecated not used except in tests - remove post-R4
				 */
				setAllFields(newFields) {
					replaceArray(self.getFields().getAll(), newFields);
				},

				_setFields( dataSourceId, entry ) {
					self.fields.set(dataSourceId, entry);
				},

				//Note: When newStartingPoints feature flag is removed, addField()
				//      should be removed. When this happens, we could rename this to
				//      addField(), which would be more consistent with deleteField()
				addToAllFields(newField){
					self.getFields().getAll().push(newField);
				},

				deleteField(id){
					//Delete from all fields
					const fields = self.getFields();
					const all = fields.getAll();
					deleteFromArray(all, field => field.id === id);

					//Delete from recent fields
					deleteFromArray(fields.recentFields, fieldId => fieldId === id);

					//Delete from interesting fields
					const focusFieldIndex = self.getFocusFieldIndex();
					const interestingFields = fields.getInteresting();
					const indexInInterestingFields = deleteFromArray(interestingFields, field => field.id === id);
					if(~indexInInterestingFields){
						if(indexInInterestingFields === focusFieldIndex){
							//The deleted field is target field
							setDefaultFocusFieldId();
						}
					}
				},

				/**
				 * @deprecated This will be going away when newStartingPoints flag is removed - we will no longer need to add interesting fields
				 */
				addField(id){
					const fields = self.getFields();
					const interestingFields = fields.getInteresting();
					const fieldIndex = interestingFields.findIndex(field => field.id === id);
					if (fieldIndex > -1) {
						if(id !== self._getFocusFieldId()) {
							self.setFocusField(id);
						}
						return true;
					} else {
						const field = self._getField(id);
						if (field) {
							interestingFields.unshift({ name: field.name, id: id });
							self.setFocusField(id);
							//Fields is a volatile object, which can do a shallow observer. instantiate new object can work around this.
							return true;
						}
						return false;
					}
				},
				setClearInput(value){
					self.clearInput = value;
				},
				resetData() {
					self.clearInput = true;
				},
				clearFields(){
					self.fields.delete(getDataSourceId());
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/FieldsModel.js